<template>
  <vue-select
    v-if="scopedSlots"
    ref="select"
    v-model="model"
    class="custom-select es-select"
    v-bind="$attrs"
    append-to-body
    :multiple="multiple"
    :calculate-position="withPopper"
    :disabled="disabled || readonly"
    :searchable="!readonly || searchable"
    @option:selected="handleSelect"
    @search:focus="openedSelect"
    @open="e => $emit('open', e)"
    @close="e => $emit('close', e)"
    @search="e => $emit('search', e)"
  >
    <template #search="{ attributes, events }">
      <input
        class="vs__search text-truncate"
        :class="{'vs__search-single': !multiple}"
        :readonly="readonly || !searchable"
        v-bind="attributes"
        v-on="events"
      >
    </template>
    <template
      #open-indicator="{ attributes }"
    >
      <span v-bind="attributes">
        <svg
          v-if="indicator"
          width="10"
          height="7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M.6 1.432l4.325 4.109L9.25 1.432"
            stroke="#6E747A"
            stroke-width="1.5"
          />
        </svg>
      </span>
    </template>
    <template
      v-for="(_, slot) in scopedSlots"
      #[slot]="props"
    >
      <slot
        :name="slot"
        v-bind="props"
      />
    </template>
  </vue-select>
</template>

<script>
import popperMixin from '../mixins/popperMixin'

export default {
  name: 'VSelect',

  mixins: [popperMixin],

  props: {
    value: {
      required: true
    },
    width: {
      type: String
    },
    offsetLeft: {
      type: Number,
      default: 0
    },
    dropDownClasses: {
      type: String,
      default: ''
    },
    indicator: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 0
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      scopedSlots: null
    }
  },

  computed: {
    model: {
      get () {
        return this.value
      },
      set (val) {
        // If it's not an array or limit is 0, pass through
        if (!Array.isArray(val) || this.limit === 0) {
          this.$emit('input', val)
          return
        }

        // For arrays, enforce the limit
        const limitedVal = val.slice(0, this.limit)
        this.$emit('input', limitedVal)
      }
    },
    isLimitReached () {
      return Array.isArray(this.value) &&
          this.limit > 0 &&
          this.value.length >= this.limit
    }
  },

  mounted () {
    let scopedSlots = {}
    for (let slot in this.$scopedSlots) {
      scopedSlots[slot] = this.$scopedSlots[slot]
    }
    this.scopedSlots = scopedSlots
  },

  methods: {
    openedSelect () {
      if (this.readonly) return
      this.$nextTick(() => {
        const menu = document.querySelector('.vs__dropdown-menu')
        if (!menu) return
        const active = menu.querySelector('.vs__dropdown-option--selected')
        if (active) {
          menu.scrollTo(0, active.offsetTop - (menu.offsetHeight / 2))
        }
        if (this.width) {
          menu.style.width = this.width
        }
        if(this.dropDownClasses){
          const cls = this.dropDownClasses.split(' ')
          for (const cl of cls) {
            menu.classList.add(cl)
          }
        }
      })
    },
    handleSelect (option) {
      if (this.readonly) return
      if (this.isLimitReached && !this.value.includes(option)) {
        // Optional: Show a warning message when limit is reached
        this.$emit('limit-reached', this.limit)
        return
      }

      this.$emit('option:selected', option)
    }
  }
}
</script>

<style>
.v-select.custom-select.es-select {
  background: none;

  .vs__open-indicator {
    display: block;
  }
}

.v-select.custom-select[multiple]:not(.select-tags) {
  height: auto !important;
  min-height: 38px;
}

.v-select.custom-select[multiple]:not(.select-tags) > .vs__dropdown-toggle {
  position: relative;
}

.v-select.custom-select[multiple] .vs__selected {
  overflow: visible;
  white-space: wrap;
  z-index: 50;
}

.vs--loading .vs__spinner{
  background-color: #fff;
}
.select-tags.vs--loading .vs__spinner{
  margin-right: -10px;
}

.v-select.custom-select.es-select .vs__dropdown-option--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.vs__search, .vs__search:focus {
  transform: scale(1);
}

.vs__dropdown-menu {
  z-index: 5000 !important;
}

.vs__search-single {
  position: absolute;
  width: 100% !important;
  left: 0;
}

/* make multi-select auto-height (vue-select uses .vs--multiple, not [multiple]) */
.v-select.custom-select.vs--multiple:not(.select-tags) {
  height: auto !important;
  min-height: 38px;
}

/* let the toggle expand vertically when there are many chips */
.v-select.custom-select.vs--multiple:not(.select-tags) > .vs__dropdown-toggle {
  position: relative;
  height: auto !important;
  min-height: 38px;
  align-items: flex-start; /* keep chips + input aligned to top */
}

/* allow selected items to wrap to new lines */
.v-select.custom-select.vs--multiple .vs__selected-options {
  display: flex;
  flex-wrap: wrap;
}

/* chip styling: allow text to wrap instead of forcing one line */
.v-select.custom-select.vs--multiple .vs__selected {
  overflow: visible;
  white-space: normal; /* was 'wrap' (invalid) */
  z-index: 50;
}

/* keep the search input usable when many chips exist */
.v-select.custom-select.vs--multiple .vs__search {
  flex: 1 0 120px;
  min-width: 120px;
}

.vs__dropdown-option.vs__dropdown-option--selected {
  display: none !important;
}

.vs--disabled .vs__clear, .vs--disabled .vs__dropdown-toggle, .vs--disabled .vs__open-indicator, .vs--disabled .vs__selected {
  background-color: #e9ecef;
}

.vs--disabled .vs__clear, .vs--disabled .vs__dropdown-toggle, .vs--disabled .vs__open-indicator, .vs--disabled .vs__search, .vs--disabled .vs__selected {
  cursor: text;
}

/* In single select, the (disabled) search input can overlay the selected label. Hide it so the selected value stays visible. */
.vs--disabled:not(.vs--multiple) .vs__search {
  background-color: transparent;
  opacity: 0;
}

/* In multi select, keep the input from painting an opaque background over chips */
.vs--disabled.vs--multiple .vs__search {
  background-color: transparent;
}
</style>
